import { useEffect, useMemo, useState } from 'react';
import type {
  DeviceSaSettings,
  SaSettingKey,
  SaSettingsPeer,
  UpdateSaSettingsRequest,
} from '@order-pilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState, Skeleton } from '@/components/States';
import {
  copySaSettings,
  fetchSaSettings,
  tenantKeys,
  updateSaSettings,
} from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

const LANGUAGES: { value: string; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية (Arabic)' },
];

/** The on/off switches, in the order the till's SA Settings shows them. */
const SWITCHES: { key: SaSettingKey; title: string; hint: string }[] = [
  {
    key: 'product_export_enabled',
    title: 'Menu download',
    hint: 'Allow downloading the price list — a Download button on Products.',
  },
  {
    key: 'token_enabled',
    title: 'Token number',
    hint: 'Every kitchen order gets a number, printed large on its KOT and bill. Restarts at 1 each shift.',
  },
  {
    key: 'cash_only_close',
    title: 'Counter close — count only cash',
    hint: 'Expected in Drawer counts only cash. Card and credit are still shown, not added.',
  },
];

const ALL_KEYS: SaSettingKey[] = [
  'max_paired_devices',
  'app_language',
  ...SWITCHES.map((s) => s.key),
];

type Draft = Record<SaSettingKey, string>;

function draftOf(data: DeviceSaSettings): Draft {
  const out = {} as Draft;
  for (const key of ALL_KEYS) {
    out[key] = data.settings.find((s) => s.key === key)?.value ?? '';
  }
  return out;
}

/**
 * This till's SA switches — the same ones as its SA Settings, every one
 * except the licence — kept in step both ways. A change here reaches the till
 * on its next sync (every few minutes, or when the app is opened); a change
 * made on the till shows here after it syncs. SA login only.
 */
export function SaSettingsSection({
  deviceId,
  deviceLabel,
  disconnected,
}: {
  deviceId: string;
  deviceLabel: string;
  disconnected: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: tenantKeys.saSettings(deviceId),
    queryFn: ({ signal }) => fetchSaSettings(deviceId, signal),
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setDraft(draftOf(query.data));
  }, [query.data]);

  const saveMutation = useMutation({
    mutationFn: (input: UpdateSaSettingsRequest) => updateSaSettings(deviceId, input),
    onSuccess: (data) => {
      setCopied(null);
      queryClient.setQueryData(tenantKeys.saSettings(deviceId), data);
    },
  });

  const data = query.data;
  const saved = data ? draftOf(data) : null;
  // The till's own −/+ range.
  const limit = Math.min(50, Math.max(1, Number(draft?.max_paired_devices) || 5));
  const changes: UpdateSaSettingsRequest = {};
  if (draft && saved) {
    for (const key of ALL_KEYS) {
      // The limit is saved clamped to the till's range, typed or not.
      const value =
        key === 'max_paired_devices' && draft[key] !== '' ? String(limit) : draft[key];
      if (value !== '' && value !== saved[key]) changes[key] = value;
    }
  }
  const dirty = Object.keys(changes).length > 0;
  const hasAny = data?.settings.some((s) => s.version > 0) ?? false;
  const lastChange = data?.settings
    .filter((s) => s.updatedAt)
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))[0];

  return (
    <SectionCard
      headingId="device-sa-settings"
      title="SA settings"
      description="This till's SA Settings, except the licence and cloud sync. A change here reaches the till on its next sync; a change made on the till shows here after it syncs."
      action={
        data && data.peers.length > 0 && hasAny ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={dirty}
            title={dirty ? 'Save first, then copy' : undefined}
            onClick={() => setCopyOpen(true)}
          >
            Copy to…
          </Button>
        ) : null
      }
    >
      {query.isError ? (
        <EmptyState title="Could not load SA settings" compact />
      ) : !draft || !data ? (
        <div className="space-y-2 px-5 py-4">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : (
        <div className="space-y-5 px-5 py-4">
          {saveMutation.isError ? (
            <Alert tone="error">{getErrorMessage(saveMutation.error)}</Alert>
          ) : null}
          {copied ? <Alert tone="success">{copied}</Alert> : null}
          {!hasAny ? (
            <Alert tone="info">
              Not synced yet — this till sends its own settings the first time it syncs. You can set
              them now and the till takes them then.
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Connected devices — tablet limit
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label="One fewer tablet"
                  disabled={disconnected || limit <= 1}
                  onClick={() => setDraft({ ...draft, max_paired_devices: String(limit - 1) })}
                >
                  −
                </Button>
                <input
                  className="input w-20 text-center"
                  inputMode="numeric"
                  value={draft.max_paired_devices}
                  disabled={disconnected}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                    setDraft({ ...draft, max_paired_devices: digits });
                  }}
                  onBlur={() =>
                    setDraft({
                      ...draft,
                      max_paired_devices: draft.max_paired_devices === '' ? '' : String(limit),
                    })
                  }
                />
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label="One more tablet"
                  disabled={disconnected || limit >= 50}
                  onClick={() => setDraft({ ...draft, max_paired_devices: String(limit + 1) })}
                >
                  +
                </Button>
              </div>
              <span className="mt-1 block text-xs text-slate-500">
                Tablets that can pair with this POS (1–50).
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Language</span>
              <select
                className="input pr-9"
                value={draft.app_language}
                disabled={disconnected}
                onChange={(e) => setDraft({ ...draft, app_language: e.target.value })}
              >
                {draft.app_language === '' ? <option value="">Not set yet</option> : null}
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {SWITCHES.map((sw) => {
              const on = draft[sw.key] === 'true';
              return (
                <li key={sw.key} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{sw.title}</p>
                    <p className="text-xs text-slate-500">{sw.hint}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={sw.title}
                    disabled={disconnected}
                    onClick={() => setDraft({ ...draft, [sw.key]: on ? 'false' : 'true' })}
                    className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                      on ? 'bg-brand-700' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        on ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant="primary"
              disabled={!dirty || saveMutation.isPending || disconnected}
              onClick={() => saveMutation.mutate(changes)}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
            {dirty ? (
              <Button size="sm" variant="ghost" onClick={() => saved && setDraft(saved)}>
                Discard
              </Button>
            ) : null}
            {data.appliedAt ? (
              data.upToDate ? (
                <Badge tone="success" dot>
                  Till up to date
                </Badge>
              ) : (
                <Badge tone="warning" dot>
                  Waiting for the till's next sync
                </Badge>
              )
            ) : (
              <Badge tone="neutral" dot>
                Never synced
              </Badge>
            )}
            <p className="hint">
              {data.appliedAt ? `Till synced ${formatDateTime(data.appliedAt)}` : null}
              {lastChange?.updatedAt
                ? ` · last changed ${formatDateTime(lastChange.updatedAt)} by ${lastChange.updatedBy}`
                : null}
            </p>
          </div>
        </div>
      )}

      {data ? (
        <CopyDialog
          open={copyOpen}
          deviceId={deviceId}
          deviceLabel={deviceLabel}
          branchId={data.branchId}
          peers={data.peers}
          onClose={() => setCopyOpen(false)}
          onDone={(message) => {
            setCopyOpen(false);
            setCopied(message);
            void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
          }}
        />
      ) : null}
    </SectionCard>
  );
}

/** "Copy to…": pick tills of this tenant, grouped by branch. */
function CopyDialog({
  open,
  deviceId,
  deviceLabel,
  branchId,
  peers,
  onClose,
  onDone,
}: {
  open: boolean;
  deviceId: string;
  deviceLabel: string;
  branchId: string;
  peers: SaSettingsPeer[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setPicked(new Set());
  }, [open]);

  const branches = useMemo(() => {
    const map = new Map<string, { name: string; peers: SaSettingsPeer[] }>();
    for (const p of peers) {
      const b = map.get(p.branchId) ?? { name: p.branchName, peers: [] };
      b.peers.push(p);
      map.set(p.branchId, b);
    }
    // This till's own branch first — the usual target.
    return [...map.entries()].sort(([a], [b]) =>
      a === branchId ? -1 : b === branchId ? 1 : 0,
    );
  }, [peers, branchId]);

  const mutation = useMutation({
    mutationFn: () => copySaSettings(deviceId, { deviceIds: [...picked] }),
    onSuccess: (res) =>
      onDone(
        res.updated === 0
          ? 'Those tills already had these settings.'
          : `Copied to ${res.updated} till${res.updated === 1 ? '' : 's'}. Each picks it up on its next sync.`,
      ),
  });

  const setMany = (ids: string[], on: boolean) => {
    const next = new Set(picked);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setPicked(next);
  };

  return (
    <ConfirmDialog
      open={open}
      title={`Copy ${deviceLabel}'s SA settings`}
      description="The chosen tills get exactly these switches. Their licence and cloud reporting are not touched."
      confirmLabel={`Copy to ${picked.size} till${picked.size === 1 ? '' : 's'}`}
      confirmVariant="primary"
      pending={mutation.isPending}
      error={mutation.isError ? getErrorMessage(mutation.error) : undefined}
      onConfirm={() => {
        if (picked.size > 0) mutation.mutate();
      }}
      onClose={onClose}
    >
      <div className="max-h-80 space-y-4 overflow-y-auto">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setMany(peers.filter((p) => p.branchId === branchId).map((p) => p.id), true)}
          >
            This branch
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setMany(peers.map((p) => p.id), true)}>
            All tills
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
            Clear
          </Button>
        </div>
        {branches.map(([id, b]) => (
          <fieldset key={id}>
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {b.name}
              {id === branchId ? ' (this branch)' : ''}
            </legend>
            <ul className="space-y-1">
              {b.peers.map((p) => (
                <li key={p.id}>
                  <label className="flex items-center gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      checked={picked.has(p.id)}
                      onChange={(e) => setMany([p.id], e.target.checked)}
                    />
                    {p.label}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ))}
      </div>
    </ConfirmDialog>
  );
}
